import { useMemo } from 'react'
import { getQuestionValue, getQuestionValues } from '../chart-aggregation'
import { formatLabel } from '@/lib/utils/formatting'
import {
  formatAssetReportLabel,
  type AssetReportContextItem,
} from '../asset-report-label'
import { TextClamp } from '../../text/TextClamp/TextClamp'
import { CHART_LIMITS } from '../../../foundations/slide-limits'
import { DECK_CHART_SERIES_COLORS } from '../../../foundations/chart-colors'
import {
  DEFAULT_RANKING_CHART_LABELS,
  RankingChartLabels,
  RankingChartView,
} from './RankingChartView'
import { rankingViewItemsFromScores } from './rankingChartViewItems'
import { LaneMergedSections } from '../LaneMergedSections'
import {
  unfoldResponsesForLane,
  type MonadicLaneScopeFact,
} from '../../../lib/monadicLaneScope'

export type {
  PodiumRankingItem,
  RankingChartLabels,
  RankingChartPodiumLabels,
  RankingChartViewPodiumProps,
  RankingChartViewProps,
} from './RankingChartView'
export {
  DEFAULT_RANKING_CHART_LABELS,
  DEFAULT_RANKING_CHART_PODIUM_LABELS,
  RankingChartView,
} from './RankingChartView'
export type { RankingChartViewItem } from './rankingChartViewItems'
export { rankingViewItemsFromScores } from './rankingChartViewItems'

/**
 * Safe division that prevents NaN/Infinity results.
 * Returns 0 if divisor is 0 or result is not finite.
 */
function safeDivide(numerator: number, divisor: number, fallback = 0): number {
  if (divisor === 0) return fallback
  const result = numerator / divisor
  return Number.isFinite(result) ? result : fallback
}

interface RankingChartProps {
  question: any
  segmentQuestion?: any
  responses: any[]
  colors?: string[]
  assetContextItems?: Array<AssetReportContextItem>
  labels?: Partial<RankingChartLabels>
  monadicLaneScope?: MonadicLaneScopeFact | null
  emptyLaneLabel?: string
}

interface RankingDataPoint {
  name: string
  label: string
  score: number
  count: number
}

interface SegmentedRankingDataPoint {
  name: string
  label: string
  segments: Record<string, { score: number; count: number }>
}

/**
 * Weighted ranking score: score(item) = Σ(count_at_rank × (N - rank + 1)) / respondents
 * Higher score = more preferred. Industry standard (SurveyMonkey, Alchemer, Survicate).
 */
function choiceDisplayLabel(
  label: string,
  value: string,
  assetContextItems?: Array<AssetReportContextItem>,
): string {
  return formatAssetReportLabel(label, assetContextItems, [value])
}

function aggregateRanking(
  question: any,
  responses: any[],
  assetContextItems?: Array<AssetReportContextItem>,
): RankingDataPoint[] {
  if (!responses || responses.length === 0) return []

  const choices = question.choices || []
  const N = choices.length
  if (N === 0) return []

  // Per-item: accumulate weighted score and count of respondents who ranked it
  const stats: Record<string, { weightedSum: number; count: number }> = {}

  choices.forEach((choice: any) => {
    const value = typeof choice === 'object' ? (choice.value ?? choice.text) : choice
    stats[String(value)] = { weightedSum: 0, count: 0 }
  })

  let totalRespondents = 0

  responses.forEach((r: any) => {
    const rankingValue = getQuestionValue(r, question)
    if (!rankingValue || !Array.isArray(rankingValue) || rankingValue.length === 0) return

    totalRespondents++

    rankingValue.forEach((item: any, index: number) => {
      const itemStr = String(item)
      const rank = index + 1
      const weight = N - rank + 1

      if (!stats[itemStr]) {
        stats[itemStr] = { weightedSum: 0, count: 0 }
      }
      stats[itemStr].weightedSum += weight
      stats[itemStr].count += 1
    })
  })

  const denominator = totalRespondents || 1

  const dataPoints: RankingDataPoint[] = Object.entries(stats)
    .filter(([_, s]) => s.count > 0)
    .map(([value, s]) => {
      let label = value
      const choice = choices.find((c: any) => {
        const cVal = typeof c === 'object' ? (c.value ?? c.text) : c
        return String(cVal) === value
      })
      if (choice) {
        label = typeof choice === 'object' ? (choice.text || choice.value) : choice
      }

      return {
        name: value,
        label: choiceDisplayLabel(String(label), value, assetContextItems),
        score: safeDivide(s.weightedSum, denominator, 0),
        count: s.count,
      }
    })

  return dataPoints.sort((a, b) => b.score - a.score)
}

/**
 * Segmented ranking: weighted score per item per segment.
 */
function aggregateRankingSegmented(
  question: any, 
  segmentQuestion: any, 
  responses: any[],
  assetContextItems?: Array<AssetReportContextItem>,
): { data: SegmentedRankingDataPoint[]; segmentNames: string[] } {
  if (!responses || responses.length === 0) return { data: [], segmentNames: [] }

  const choices = question.choices || []
  const N = choices.length
  if (N === 0) return { data: [], segmentNames: [] }

  const segmentGroups: Record<string, any[]> = {}

  responses.forEach(r => {
    const segmentValues = new Set(getQuestionValues(r, segmentQuestion).map(String))
    segmentValues.forEach((segmentStr) => {
      if (!segmentGroups[segmentStr]) segmentGroups[segmentStr] = []
      segmentGroups[segmentStr].push(r)
    })
  })

  const sortedSegmentNames = Object.entries(segmentGroups)
    .sort((a, b) => b[1].length - a[1].length)
    .slice(0, CHART_LIMITS.rankingMaxSegments)
    .map(([name]) => name)

  const dataMap: Record<string, SegmentedRankingDataPoint> = {}

  choices.forEach((choice: any) => {
    const value = typeof choice === 'object' ? (choice.value ?? choice.text) : choice
    const label = typeof choice === 'object' ? (choice.text || choice.value) : choice
    dataMap[String(value)] = {
      name: String(value),
      label: choiceDisplayLabel(String(label), String(value), assetContextItems),
      segments: {},
    }
  })

  sortedSegmentNames.forEach(segmentName => {
    const segmentResponses = segmentGroups[segmentName]
    let segRespondents = 0
    const weightedSums: Record<string, number> = {}
    let rankedCount = 0

    segmentResponses.forEach((r: any) => {
      const rankingValue = getQuestionValue(r, question)
      if (!Array.isArray(rankingValue) || rankingValue.length === 0) return
      segRespondents++

      rankedCount++
      rankingValue.forEach((item: any, index: number) => {
        const itemStr = String(item)
        const weight = N - (index + 1) + 1
        weightedSums[itemStr] = (weightedSums[itemStr] || 0) + weight
      })
    })

    const denominator = rankedCount || 1
    Object.entries(weightedSums).forEach(([choiceValue, wSum]) => {
      if (dataMap[choiceValue]) {
        dataMap[choiceValue].segments[segmentName] = {
          score: safeDivide(wSum, denominator, 0),
          count: rankedCount,
        }
      }
    })
  })

  const dataArray = Object.values(dataMap)
  dataArray.sort((a, b) => {
    const scoreA = Object.values(a.segments).reduce((sum, s) => sum + s.score, 0) / (Object.keys(a.segments).length || 1)
    const scoreB = Object.values(b.segments).reduce((sum, s) => sum + s.score, 0) / (Object.keys(b.segments).length || 1)
    return scoreB - scoreA
  })

  return { data: dataArray, segmentNames: sortedSegmentNames }
}

/**
 * @deprecated 2026-10-01: legacy reader only. Do not use in new code; draw the shared
 * plots in `packages/deck-ui/src/plots` instead. Kept so old studies the figure service
 * refuses (and cards whose figure fails) still draw exactly as before.
 * Delete after 2027-01-01, together with the legacy fallback in QuestionCard.
 */
export function RankingChart(props: RankingChartProps) {
  const { question, responses, monadicLaneScope, assetContextItems, emptyLaneLabel } = props
  if (unfoldResponsesForLane(question, responses, monadicLaneScope).length > 0) {
    return (
      <LaneMergedSections
        question={question}
        responses={responses}
        monadicLaneScope={monadicLaneScope}
        assetContextItems={assetContextItems}
        testIdPrefix="ranking-chart"
        emptyLabel={emptyLaneLabel}
      >
        {(unfolded) => (
          <RankingChartBody {...props} responses={unfolded} segmentQuestion={undefined} monadicLaneScope={null} />
        )}
      </LaneMergedSections>
    )
  }
  return <RankingChartBody {...props} />
}

function RankingChartBody({
  question,
  segmentQuestion,
  responses,
  colors: colorsProp,
  assetContextItems,
  labels: labelsProp,
}: RankingChartProps) {
  const labels: RankingChartLabels = {
    ...DEFAULT_RANKING_CHART_LABELS,
    ...labelsProp,
  }
  const effectiveBarColors = colorsProp || DECK_CHART_SERIES_COLORS
  const isSegmented = !!segmentQuestion

  const data = useMemo(() => {
    if (isSegmented) return []
    return aggregateRanking(question, responses, assetContextItems)
  }, [question, responses, isSegmented, assetContextItems])

  const segmentedResult = useMemo(() => {
    if (!isSegmented) return { data: [], segmentNames: [] }
    return aggregateRankingSegmented(question, segmentQuestion, responses, assetContextItems)
  }, [question, segmentQuestion, responses, isSegmented, assetContextItems])

  const isEmpty = isSegmented 
    ? segmentedResult.data.length === 0 
    : data.length === 0

  if (isEmpty) {
    return (
      <div
        className="flex items-center justify-center text-muted-foreground min-h-48"
      >
        No ranking data available
      </div>
    )
  }

  const choices = question.choices || []

  const getBarColor = (index: number) => effectiveBarColors[index % effectiveBarColors.length]

  // =====================================================
  // SEGMENTED VIEW
  // =====================================================
  if (isSegmented) {
    const { data: segData, segmentNames } = segmentedResult
    
    return (
      /* No max-height here. This branch renders only in the report's survey-data
         tab, which scrolls as a page, and the item / segment caps above already
         bound the block. A clamp cut the tail off with no scrollbar to reveal it. */
      <div
        className="w-full space-y-8 overflow-hidden pr-4"
      >
        {segData.slice(0, CHART_LIMITS.rankingSegmentedItems).map((item, index) => (
          <div key={item.name} className="space-y-3">
            {/* Item Title */}
            <div className="flex items-center gap-3">
              <TextClamp
                maxLines={2}
                className="text-foreground flex-1 leading-snug text-sm font-semibold"
                title={formatLabel(item.label)}
              >
                {formatLabel(item.label)}
              </TextClamp>
            </div>

            {/* Bars per segment */}
            <div className="space-y-3">
              {segmentNames.map((segmentName, segmentIdx) => {
                const segmentData = item.segments[segmentName]
                const maxScore = choices.length
                const barPercent = segmentData ? safeDivide(segmentData.score, maxScore, 0) * 100 : 0
                
                return (
                  <div key={segmentName} className="space-y-1">
                    <div className="flex items-center justify-between gap-2">
                      <TextClamp
                        maxLines={1}
                        className="text-foreground flex-1 leading-snug text-sm font-semibold"
                      >
                        {formatLabel(segmentName)}
                      </TextClamp>
                      <span className="text-muted-foreground tabular-nums shrink-0 text-sm font-semibold">
                        {segmentData ? segmentData.score.toFixed(1) : '-'}
                      </span>
                    </div>
                    <div className="relative h-5">
                      <div className="absolute inset-0 bg-muted/40 rounded-md" />
                      <div 
                        className="absolute left-0 top-1 bottom-1 rounded transition-all duration-500 ease-out min-w-1"
                        style={{
                          width: `${barPercent}%`,
                          backgroundColor: getBarColor(segmentIdx),
                        }}
                        title={labels.formatSegmentBarTitle(
                          segmentName,
                          segmentData?.score.toFixed(1) ?? '-',
                          segmentData?.count || 0,
                        )}
                      />
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        ))}
        
        {segData.length > CHART_LIMITS.rankingSegmentedItems && (
          <div className="text-center pt-4">
            <p className="text-sm text-muted-foreground italic">
              {labels.formatShowingTopItems(CHART_LIMITS.rankingSegmentedItems, segData.length)}
            </p>
          </div>
        )}
      </div>
    )
  }

  // =====================================================
  // NON-SEGMENTED VIEW
  // =====================================================
  const items = rankingViewItemsFromScores(
    data.map((item) => ({ label: item.label, score: item.score })),
  )

  return (
    <RankingChartView
      items={items}
      maxScore={choices.length}
      colors={colorsProp}
      labels={labels}
    />
  )
}
